import React, { useState } from 'react';
import './ResultsPhase.css';
import Button from '../ui/Button';
import Card from '../ui/Card';
import { useGame } from '../../contexts/GameContext';
import { resetGame, leaveLobby } from '../../firebase/services';
import { useNavigate } from 'react-router-dom';
import { getCardById } from '../../data/cards';

const ResultsPhase = () => {
  const { lobby, playerId, isHost, leaveGame } = useGame();
  const navigate = useNavigate();
  const [showAllCards, setShowAllCards] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  if (!lobby || !lobby.gameResults) {
    return <div className="results-loading">Sonuçlar yükleniyor...</div>;
  }

  const { 
    winningTeam,
    winMessage,
    finalCards,
    allEliminatedIds
  } = lobby.gameResults;

  const totalRounds = lobby.roundNumber || 1;

  const getWinnerDisplayInfo = () => {
    switch (winningTeam) {
      case 'werewolf':
        return { title: 'Kurt Takımı Kazandı!', className: 'results-winner--werewolf' };
      case 'villager':
        return { title: 'Köylü Takımı Kazandı!', className: 'results-winner--villager' };
      case 'tanner':
        return { title: 'Düzenbaz Kazandı!', className: 'results-winner--tanner' };
      default:
        return { title: 'Oyun Bitti', className: '' };
    }
  };

  const winnerInfo = getWinnerDisplayInfo();

  const handleShowAllCards = () => setShowAllCards(true);

  const handleResetGame = async () => {
    if (!isHost || isResetting) return;
    setIsResetting(true);
    try {
      await resetGame(lobby.code);
    } catch (error) {
      console.error('Error resetting game:', error);
      setIsResetting(false);
    }
  };

  const handleLeaveLobby = async () => {
    if (isLeaving) return;
    setIsLeaving(true);
    try {
      await leaveLobby(lobby.code, playerId);
      leaveGame();
      navigate('/');
    } catch (error) {
      console.error('Error leaving lobby:', error);
      setIsLeaving(false);
    }
  };

  const eliminatedCards = (finalCards || []).filter(pc => pc.isEliminated);

  return (
    <div className="results-phase">
      <div className={`results-winner ${winnerInfo.className}`}>
        <h2 className="results-winner-title">{winnerInfo.title}</h2>
        <p className="results-winner-description">{winMessage}</p>
        <p className="results-round-count">{totalRounds} tur oynandı</p>
      </div>

      {eliminatedCards.length > 0 && (
        <div className="results-voted-player">
          <h3>Elenen Oyuncular</h3>
          {eliminatedCards.map(pc => {
            const cardInfo = getCardById(pc.cardId);
            return (
              <div key={pc.playerId} className="results-voted-player-info">
                <div className="results-voted-player-name">{pc.playerName}</div>
                {cardInfo && (
                  <div className="results-voted-player-card">
                    <Card cardId={pc.cardId} revealed={true} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showAllCards ? (
        <div className="results-all-cards">
          <h3>Tüm Oyuncular ve Kartları</h3>
          <div className="results-players-grid">
            {(finalCards || []).map(playerCard => {
              const card = getCardById(playerCard.cardId);
              const teamClass = card ? `results-player-team--${card.team}` : '';
              const isExecuted = playerCard.isEliminated;
              
              return (
                <div key={playerCard.playerId} 
                  className={`results-player ${teamClass} ${isExecuted ? 'results-player--executed' : ''}`}
                >
                  <div className="results-player-info">
                    <div className="results-player-avatar">
                      {playerCard.playerName.charAt(0)}
                    </div>
                    <div className="results-player-name">
                      {playerCard.playerName}
                      {isExecuted && <span className="results-player-executed-tag">Elendi</span>}
                    </div>
                  </div>
                  <div className="results-player-card">
                    <Card cardId={playerCard.originalCardId || playerCard.cardId} revealed={true} />
                  </div>
                  {playerCard.originalCardId && playerCard.originalCardId !== playerCard.cardId && (
                    <div className="results-player-final-card">
                      <div className="results-player-final-card-label">Son kartı:</div>
                      <Card cardId={playerCard.cardId} revealed={true} size="small" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          
          <div className="results-center-cards">
            <h3>Ortadaki Kartlar</h3>
            <div className="results-center-cards-grid">
              {lobby.centerCards && lobby.centerCards.map((cardId, index) => (
                <div key={index} className="results-center-card">
                  <div className="results-center-card-label">Orta {index + 1}</div>
                  <Card cardId={cardId} revealed={true} />
                </div>
              ))}
            </div>
          </div>

          <div className="results-rules">
            <h3>Kazanma Kuralları</h3>
            <ul className="results-rules-list">
              <li><span className="results-rules-condition">Tüm kurtlar elenirse:</span> Köylü Takımı kazanır</li>
              <li><span className="results-rules-condition">Kurtlar &ge; Köylüler olursa:</span> Kurt Takımı kazanır</li>
              <li><span className="results-rules-condition">Düzenbaz elenirse:</span> Düzenbaz kazanır, oyun biter</li>
              <li><span className="results-rules-condition">Eşit oy olursa:</span> Kimse elenmez, oyun devam eder</li>
            </ul>
          </div>
        </div>
      ) : (
        <div className="results-reveal-cards">
          <Button 
            onClick={handleShowAllCards}
            variant="secondary"
            fullWidth={true}
          >
            Tüm Kartları ve Kuralları Göster
          </Button>
        </div>
      )}
      
      <div className="results-actions">
        {isHost && (
          <Button 
            onClick={handleResetGame}
            variant="primary"
            disabled={isResetting}
            fullWidth={true}
          >
            {isResetting ? 'Oyun Sıfırlanıyor...' : 'Yeni Oyun Başlat'}
          </Button>
        )}
        
        <Button 
          onClick={handleLeaveLobby}
          variant="secondary"
          disabled={isLeaving}
          fullWidth={true}
        >
          {isLeaving ? 'Çıkış Yapılıyor...' : 'Lobiden Ayrıl'}
        </Button>
      </div>
    </div>
  );
};

export default ResultsPhase;
